import React from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
    Button,
    Paper,
    Box,
    Divider,
    Typography,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import { getSingleBroadcastMessage } from 'core/API/broadcast';
import { formatDateMMDDYYYY, formatDateTime12Hour } from 'shared/utils/dateUtils';
import { usePermissionCheck } from 'core/hooks/usePermissionCheck';

type RouteParams = { id?: string };

const ViewBroadcast: React.FC = () => {
    const history = useHistory();
    const { id } = useParams<RouteParams>();
    const [previewImage, setPreviewImage] = React.useState<{ url: string; alt: string } | null>(null);

    // Broadcast permission checks (module-level, like Logbook)
    const { hasModuleViewPermission } = usePermissionCheck();
    const canViewBroadcastMy = hasModuleViewPermission('Broadcast - My');
    const canViewBroadcastAll = hasModuleViewPermission('Broadcast - All');
    const canViewBroadcastOrganization = hasModuleViewPermission('Broadcast - Organization');
    const canViewBroadcast = canViewBroadcastMy || canViewBroadcastAll || canViewBroadcastOrganization;

    // Fetch broadcast data
    const { data: broadcastData, isLoading, error } = useQuery(
        ['broadcast', id],
        () => getSingleBroadcastMessage(Number(id)),
        {
            enabled: !!id,
        }
    );

    const handleBackClick = () => {
        history.push(ROUTES.BROADCAST);
    };

    // Helper function to get receiver name
    const getReceiverName = (): string => {
        if (!broadcastData) return 'N/A';
        
        if (broadcastData.receiverUser) {
            const user = broadcastData.receiverUser;
            return `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'N/A';
        } else if (broadcastData.receiverGroup) {
            const group = broadcastData.receiverGroup;
            return group.groupName || `Group ${group.id}` || 'N/A';
        }
        return 'N/A';
    };

    // Helper function to format date and time as MM/DD/YYYY HH:MM:SS AM/PM
    const formatDateTimeFromAPI = (createdDate: string | null | undefined, createdTime: string | null | undefined): string => {
        if (createdDate && createdTime) {
            return formatDateTime12Hour(createdDate, createdTime);
        } else if (createdDate) {
            return formatDateTime12Hour(createdDate, null);
        }
        return 'N/A';
    };

    // Helper function to get priority label
    const getPriorityLabel = (): string => {
        if (!broadcastData?.priority) return 'N/A';
        
        if (typeof broadcastData.priority === 'string') {
            return broadcastData.priority.split('/').pop() || 'N/A';
        }
        return broadcastData.priority.label || broadcastData.priority.name || 'N/A';
    };

    // Helper function to get sender name
    const getSenderName = (): string => {
        if (!broadcastData?.sender) return 'N/A';
        const sender = broadcastData.sender;
        return `${sender.firstName || ''} ${sender.lastName || ''}`.trim() || 'N/A';
    };

    // Preprocess HTML content to preserve tab-based spacing
    const preprocessContent = (html: string): string => {
        if (!html) return html;
        return html.replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;');
    };

    const getAttachments = () => {
        const raw = (broadcastData as any)?.attachments;
        if (!raw) return [] as any[];

        if (Array.isArray(raw)) {
            return raw;
        }

        if (Array.isArray(raw.member)) {
            return raw.member;
        }

        return [] as any[];
    };

    const attachments = getAttachments();

    // Loading state
    // Check permissions before loading
    if (!canViewBroadcast) {
        return (
            <Paper elevation={1} style={{ padding: '24px', maxWidth: '100%', margin: '0 auto' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Button
                        style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white' }}
                        onClick={handleBackClick}
                    >
                        Back
                    </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <Typography variant="h6" color="error">
                        You do not have permissions to view broadcasts.
                    </Typography>
                </Box>
            </Paper>
        );
    }

    if (isLoading) {
        return (
            <Paper elevation={1} style={{ padding: '24px', maxWidth: '100%', margin: '0 auto' }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Paper>
        );
    }

    // Error state
    if (error || !broadcastData) {
        return (
            <Paper elevation={1} style={{ padding: '24px', maxWidth: '100%', margin: '0 auto' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Button
                        style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white' }}
                        onClick={handleBackClick}
                    >
                        Back
                    </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <Typography color="error">
                        {error ? 'Error loading broadcast' : 'Broadcast not found'}
                    </Typography>
                </Box>
            </Paper>
        );
    }

    return (
        <Paper elevation={1} style={{ padding: '24px', maxWidth: '100%', margin: '0 auto' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Button
                    style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white' }}
                    onClick={handleBackClick}
                >
                    Back
                </Button>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Email-like View Mode */}
            <Box
                sx={{
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    padding: '20px',
                    maxWidth: '100%',
                }}
            >
                {/* Subject - Large Bold */}
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 'bold',
                        fontSize: '1.5rem',
                        mb: 3,
                        color: '#333',
                    }}
                >
                    {broadcastData.title || 'No Subject'}
                </Typography>

                {/* Header Info */}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 3,
                        mb: 3,
                        pb: 2,
                        borderBottom: '1px solid #eee',
                    }}
                >
                    <Box>
                        <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
                            From:
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#333' }}>
                            {getSenderName()}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
                            To:
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#333' }}>
                            {getReceiverName()}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
                            Priority:
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#333' }}>
                            {getPriorityLabel()}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
                            Date & Time:
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#333' }}>
                            {formatDateTimeFromAPI(
                                broadcastData.createdDate || broadcastData.createdAt || broadcastData.created_at,
                                broadcastData.createdTime
                            )}
                        </Typography>
                    </Box>
                </Box>

                {/* Attachments (images above, text below) */}
                {attachments.length > 0 && (
                    <Box sx={{ mt: 3, mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {attachments.map((att: any) => {
                            const isImage = typeof att.mimeType === 'string' && att.mimeType.startsWith('image/');
                            const fileName = att.fileName || att.name || `Attachment ${att.id ?? ''}`;
                            const imageBase = (process.env.REACT_APP_IMAGE_URL || process.env.REACT_APP_URL || '').replace(/\/$/, '');
                            const fileUrl = `${imageBase}/attachments/${fileName}`;

                            if (isImage) {
                                return (
                                    <Box key={att.id || att['@id']} sx={{ textAlign: 'left' }}>
                                        <Box
                                            component="img"
                                            src={fileUrl}
                                            alt={fileName}
                                            onClick={() => setPreviewImage({ url: fileUrl, alt: fileName })}
                                            sx={{
                                                width: 120,
                                                height: 120,
                                                objectFit: 'cover',
                                                borderRadius: '4px',
                                                border: '1px solid #e0e0e0',
                                                mb: 1,
                                                cursor: 'pointer',
                                            }}
                                        />
                                    </Box>
                                );
                            }

                            return (
                                <Box key={att.id || att['@id']} sx={{ textAlign: 'left' }}>
                                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                                        Attachment
                                    </Typography>
                                    <Typography variant="body2">
                                        <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                                            {fileName}
                                        </a>
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                )}

                {/* Content Area */}
                <Box
                    sx={{
                        mt: 2,
                        '& .ql-editor': {
                            padding: 0,
                            fontSize: '1rem',
                            fontFamily: 'inherit',
                            minHeight: 'auto',
                        },
                        '& .ql-container': {
                            border: 'none',
                            fontSize: '1rem',
                            fontFamily: 'inherit',
                        },
                        '& .ql-toolbar': {
                            display: 'none',
                        },
                        '& img': {
                            maxWidth: '100%',
                            height: 'auto',
                            margin: '10px 0',
                        },
                        '& a': {
                            color: '#1976d2',
                            textDecoration: 'underline',
                        },
                    }}
                >
                    <ReactQuill
                        value={preprocessContent(broadcastData.content || '')}
                        readOnly={true}
                        theme="snow"
                        modules={{ toolbar: false }}
                    />
                </Box>
            </Box>

            {/* Image preview modal */}
            <Dialog
                open={!!previewImage}
                onClose={() => setPreviewImage(null)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" noWrap sx={{ mr: 2 }}>
                        {previewImage?.alt}
                    </Typography>
                    <IconButton onClick={() => setPreviewImage(null)} size="small">
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {previewImage && (
                        <Box
                            component="img"
                            src={previewImage.url}
                            alt={previewImage.alt}
                            sx={{
                                width: '100%',
                                height: 'auto',
                                maxHeight: '80vh',
                                objectFit: 'contain',
                                display: 'block',
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </Paper>
    );
};

export default ViewBroadcast;

